import { useContext, useEffect, useState } from "react";
import { useConstants } from "../../hooks/UseConstants";
import AuthContext from "../../context/AuthContext";
import {
    Box,
    Button,
    CircularProgress,
    Grid,
    Paper,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import PieChartWithCenterLabel from "../../components/PieCenterLabel";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useTableStyles } from "../../hooks/UseTableStyles";
import { useWaits } from "../../hooks/UseWait";
import Fetch from "../../services/Fetch";
import SnackbarAlert from "../../components/SnackBar";
import useSnackBar from "../../hooks/UseSnackBar";
import SimpleAreaChart from "../../components/SimpleAreaChart";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import AdminPageHero from "../../components/AdminPageHero";
import PageContent from "../../components/PageContent";
import DashboardStatCard from "../../components/cards/DashboardStatCard";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import ScrollReveal from "../../components/motion/ScrollReveal";
import PercentChangeFooter from "../../helper/PercentChangeFooter";
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import CastForEducationOutlinedIcon from '@mui/icons-material/CastForEducationOutlined';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';

function Reports() {
    const { host, language } = useConstants();
    const isAr = language === 'ar';
    const { wait } = useContext(AuthContext);
    const { StyledTableCell, StyledTableRow } = useTableStyles();
    const { getWait, setGetWait, filterWait, setFilterWait } = useWaits();
    const { openSnackBar, type, message, setOpenSnackBar } = useSnackBar();
    const navigate = useNavigate();
    const [dashboard, setDashboard] = useState(null);
    const [salesCards, setSalesCards] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [fromDate, setFromDate] = useState('2026-01-01');
    const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));

    const loadReports = async () => {
        const params = new URLSearchParams();
        if (fromDate) params.set('from', fromDate);
        if (toDate) params.set('to', toDate);
        const dateQuery = params.toString() ? `?${params.toString()}` : '';
        const [dashRes, salesRes] = await Promise.all([
            Fetch(host + `/admin/reports/dashboard${dateQuery}`, 'GET', null),
            Fetch(host + '/reports/sales?is_detailed=1&per_page=8', 'GET', null),
        ]);

        if (dashRes.status === 200) {
            setDashboard(dashRes.data?.data || dashRes.data);
        }

        if (salesRes.status === 200) {
            const payload = salesRes.data?.data;
            if (Array.isArray(payload)) {
                setSalesCards(payload[1] || null);
                const txBlock = payload[0];
                setTransactions(Array.isArray(txBlock?.data) ? txBlock.data : []);
            } else if (payload) {
                setSalesCards(payload.cards || payload);
            }
        }

        setGetWait(false);
        setFilterWait(false);
    };

    useEffect(() => {
        if (!wait) {
            setGetWait(true);
            loadReports();
        }
    }, [wait]);

    const applyFilter = async () => {
        setFilterWait(true);
        await loadReports();
    };

    const topCourse = dashboard?.top_courses?.[0];
    const topPath = dashboard?.top_paths?.[0];
    const categories = dashboard?.categories || [];
    const monthlyChart = dashboard?.monthly_chart || [];
    const hasCategories = categories.some((c) => (c.count || 0) > 0);
    const hasChart = monthlyChart.length > 0;

    const courseName = (item) => (item ? (isAr ? (item.name_ar || item.name_en) : (item.name_en || item.name_ar)) : '—');

    const reportLinks = [
        { id: 'students_report', titleId: 'students', descId: 'students_report_desc', path: '/students', type: 'PDF' },
        { id: 'teachers_report', titleId: 'teachers', descId: 'teachers_report_desc', path: '/teachers', type: 'Excel' },
        { id: 'sales_report', titleId: 'total_sales', descId: 'sales_report_desc', path: '/financial', type: 'PDF' },
    ];

    return (
        <>
            <PageContent wait={wait} loading={getWait && !wait}>
                <AdminPageHero />

                <ScrollReveal>
                    <Box className="flex flex-wrap gap-2 mb-3">
                        <DashboardStatCard
                            label={<FormattedMessage id="total_sales" />}
                            value={`${salesCards?.total_sales?.total ?? 0}`}
                            icon={<AttachMoneyOutlinedIcon />}
                            accent="green"
                            footer={<PercentChangeFooter percent={salesCards?.total_sales?.percent ?? 0} />}
                            onClick={() => navigate('/financial')}
                            className="flex-[1_1_22%] min-w-[160px] mx-0 max-sm:flex-[1_1_48%]"
                        />
                        <DashboardStatCard
                            label={<FormattedMessage id="total_courses" />}
                            value={dashboard?.cards?.courses?.total ?? 0}
                            icon={<CastForEducationOutlinedIcon />}
                            accent="teal"
                            footer={<PercentChangeFooter percent={dashboard?.cards?.courses?.percent ?? 0} />}
                            onClick={() => navigate('/courses')}
                            className="flex-[1_1_22%] min-w-[160px] mx-0 max-sm:flex-[1_1_48%]"
                        />
                        <DashboardStatCard
                            label={<FormattedMessage id="top_selling_course" />}
                            value={courseName(topCourse)}
                            icon={<StarOutlineIcon />}
                            accent="sky"
                            onClick={() => navigate('/courses')}
                            className="flex-[1_1_22%] min-w-[160px] mx-0 max-sm:flex-[1_1_48%]"
                        />
                        <DashboardStatCard
                            label={<FormattedMessage id="top_selling_path" />}
                            value={courseName(topPath)}
                            icon={<AccountTreeOutlinedIcon />}
                            accent="blue"
                            onClick={() => navigate('/courses')}
                            className="flex-[1_1_22%] min-w-[160px] mx-0 max-sm:flex-[1_1_48%]"
                        />
                    </Box>
                </ScrollReveal>

                <ScrollReveal delay={60}>
                    <DashboardPanelCard
                        title={<FormattedMessage id="filtering" />}
                        className="mx-0 !mb-4"
                    >
                        <Box
                            className="flex flex-wrap items-end gap-3"
                            sx={{ flexDirection: isAr ? 'row' : 'row-reverse', justifyContent: 'flex-start' }}
                        >
                            <TextField
                                type="date"
                                size="small"
                                label={<FormattedMessage id="from" />}
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                            />
                            <TextField
                                type="date"
                                size="small"
                                label={<FormattedMessage id="to" />}
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                            />
                            <Button
                                variant="contained"
                                onClick={applyFilter}
                                disabled={filterWait}
                                startIcon={filterWait ? null : <FilterAltOutlinedIcon />}
                                sx={{ minWidth: 140, fontWeight: 700 }}
                            >
                                {filterWait ? <CircularProgress size={20} color="inherit" /> : <FormattedMessage id="filtering" />}
                            </Button>
                        </Box>
                    </DashboardPanelCard>
                </ScrollReveal>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 12, lg: 7 }}>
                        <ScrollReveal delay={80}>
                            <DashboardPanelCard
                                title={<FormattedMessage id="sales_growth_chart" />}
                                subtitle={isAr ? 'تسجيلات ومبيعات حسب الفترة' : 'Registrations and sales over time'}
                                className="mx-0 h-full"
                            >
                                {hasChart ? (
                                    <Box sx={{ width: '100%', overflow: 'hidden' }}>
                                        <SimpleAreaChart monthlyChart={monthlyChart} />
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 8, textAlign: 'center' }}>
                                        <FormattedMessage id="nothing" />
                                    </Typography>
                                )}
                            </DashboardPanelCard>
                        </ScrollReveal>
                    </Grid>
                    <Grid size={{ xs: 12, lg: 5 }}>
                        <ScrollReveal delay={120}>
                            <DashboardPanelCard
                                title={<FormattedMessage id="courses_by_category" />}
                                subtitle={<FormattedMessage id="main_categories" />}
                                className="mx-0 h-full"
                            >
                                {hasCategories ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', overflow: 'hidden', py: 1 }}>
                                        <PieChartWithCenterLabel
                                            values={categories.map((c) => c.percent ?? 0)}
                                            labels={categories.map((c) => (isAr ? c.name_ar : c.name_en))}
                                            counts={categories.map((c) => c.count ?? 0)}
                                        />
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 8, textAlign: 'center' }}>
                                        <FormattedMessage id="nothing" />
                                    </Typography>
                                )}
                            </DashboardPanelCard>
                        </ScrollReveal>
                    </Grid>
                </Grid>

                <ScrollReveal delay={140}>
                    <DashboardPanelCard title={<FormattedMessage id="reports_center" />} className="mx-0">
                        <TableContainer component={Paper} sx={{ boxShadow: 'none', overflowX: 'auto' }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell align={isAr ? 'right' : 'left'}><FormattedMessage id="report_type" /></StyledTableCell>
                                        <StyledTableCell align={isAr ? 'right' : 'left'}><FormattedMessage id="report_content" /></StyledTableCell>
                                        <StyledTableCell align={isAr ? 'right' : 'left'}><FormattedMessage id="file_type" /></StyledTableCell>
                                        <StyledTableCell align={isAr ? 'right' : 'left'}><FormattedMessage id="procedures" /></StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reportLinks.map((r) => (
                                        <StyledTableRow key={r.id}>
                                            <StyledTableCell align={isAr ? 'right' : 'left'}><FormattedMessage id={r.titleId} /></StyledTableCell>
                                            <StyledTableCell align={isAr ? 'right' : 'left'}><FormattedMessage id={r.descId} /></StyledTableCell>
                                            <StyledTableCell align={isAr ? 'right' : 'left'}>{r.type}</StyledTableCell>
                                            <StyledTableCell align={isAr ? 'right' : 'left'}>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={() => navigate(r.path)}
                                                    endIcon={<FileDownloadOutlinedIcon />}
                                                >
                                                    <FormattedMessage id="view_report" />
                                                </Button>
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </DashboardPanelCard>
                </ScrollReveal>

                {transactions.length > 0 && (
                    <ScrollReveal delay={160}>
                        <DashboardPanelCard
                            title={<FormattedMessage id="recent_sales" />}
                            className="mx-0 mt-3"
                        >
                            <TableContainer component={Paper} sx={{ boxShadow: 'none', overflowX: 'auto' }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell align={isAr ? 'right' : 'left'}><FormattedMessage id="transaction_id" /></StyledTableCell>
                                            <StyledTableCell align={isAr ? 'right' : 'left'}><FormattedMessage id="amount" /></StyledTableCell>
                                            <StyledTableCell align={isAr ? 'right' : 'left'}><FormattedMessage id="transaction_date" /></StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {transactions.map((tx) => (
                                            <StyledTableRow key={tx.id}>
                                                <StyledTableCell align={isAr ? 'right' : 'left'}>{tx.id}</StyledTableCell>
                                                <StyledTableCell align={isAr ? 'right' : 'left'}>{tx.amount}</StyledTableCell>
                                                <StyledTableCell align={isAr ? 'right' : 'left'}>{tx.created_at}</StyledTableCell>
                                            </StyledTableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </DashboardPanelCard>
                    </ScrollReveal>
                )}
            </PageContent>

            <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
        </>
    );
}

export default Reports;
